// Conversations browser — read-side UI for the chat-capture pipeline.
// Two-pane layout:
//   left:  filter bar + paginated conversation list
//   right: full transcript of the selected conversation, plus an
//          "Escalate to Case" button that opens a modal
//
// Phase 4 of squish-docs/architecture/assistant-conversation-capture.md.
// Calls the three Phase 3 endpoints (fetchConversations, getConversation,
// escalateConversation) which are wrapped in src/api.js.

import { useState, useEffect, useCallback } from 'react';
import { fetchConversations, getConversation, escalateConversation } from '../api';

const SOURCES = ['', 'FOS', 'SSP', 'MOBILE'];
const INTENTS = ['', 'DOMAIN_SEARCH', 'SALES', 'TICKET', 'HUMAN', 'GENERAL'];
const PAGE_SIZE = 50;

const ROLE_COLOR = {
  user:      { bg: 'rgba(99,102,241,0.12)', border: 'rgba(99,102,241,0.3)', label: '#818cf8' },
  assistant: { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.1)', label: 'rgba(255,255,255,0.7)' },
  system:    { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', label: '#fbbf24' },
  tool:      { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.25)', label: '#34d399' },
};

const SOURCE_LABEL = { FOS: 'squish.co', SSP: 'ssp.squish.co', MOBILE: 'Mobile' };

function fmtDate(s) {
  if (!s) return '';
  try {
    return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return s; }
}

function fmtTokens(n) {
  if (n == null) return '—';
  if (n < 1000) return String(n);
  return (n / 1000).toFixed(1) + 'k';
}

// `accountId` pins the browser to one customer — the account page passes it so
// a rep can read what the assistant actually said to THIS person without
// hunting the global list. The pin is re-applied on every load rather than
// only seeded into filter state, so no filter change can widen an
// account-scoped view into every conversation on the org while the page frame
// still says "this customer". FilterBar exposes no account input, so there is
// nothing for a rep to clear.
export default function ConversationsBrowser({ accountId = null, height = null }) {
  const [filters, setFilters]       = useState({ source: '', intent: '', accountId: accountId || '', since: '' });
  const [list, setList]             = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError]   = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail]         = useState(null);  // { conversation, messages }
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [showEscalate, setShowEscalate] = useState(false);

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const cleaned = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      // The pin is re-applied on every load, not just at mount, so no later
      // filter change can drop it.
      if (accountId) cleaned.accountId = accountId;
      const data = await fetchConversations({ ...cleaned, limit: PAGE_SIZE });
      setList(data.conversations || []);
    } catch (err) {
      setListError(err.message);
    } finally {
      setListLoading(false);
    }
  }, [filters, accountId]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    setDetailError('');
    getConversation(selectedId)
      .then(setDetail)
      .catch(err => setDetailError(err.message))
      .finally(() => setDetailLoading(false));
  }, [selectedId]);

  const handleEscalateSuccess = (caseId) => {
    setShowEscalate(false);
    // Patch the local detail so the UI reflects "already escalated" without a refetch
    setDetail((d) => d && ({ ...d, conversation: { ...d.conversation, Resolved_To_Case__c: caseId } }));
    // Patch the list row too
    setList((rows) => rows.map((r) => r.Id === selectedId ? { ...r, Resolved_To_Case__c: caseId } : r));
  };

  return (
    <div className="convo-browser" style={{ display: 'flex', gap: 16, height: height || 'calc(100vh - 240px)', minHeight: 420 }}>
      {/* ── LEFT PANE: filters + list ─────────────────────────────────── */}
      <div className="convo-list" style={{ width: 380, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FilterBar filters={filters} onChange={setFilters} onRefresh={loadList} />
        <div style={{
          flex: 1, overflowY: 'auto',
          border: '1px solid var(--border)', borderRadius: 10,
          background: 'var(--bg2)',
        }}>
          {listLoading && list.length === 0 && (
            <div style={{ padding: 20, color: 'var(--text3)', fontSize: 13, textAlign: 'center' }}>Loading…</div>
          )}
          {listError && (
            <div style={{ padding: 14, color: '#f87171', fontSize: 13 }}>{listError}</div>
          )}
          {!listLoading && !listError && list.length === 0 && (
            <div style={{ padding: 20, color: 'var(--text3)', fontSize: 13, textAlign: 'center' }}>
              {accountId
                ? 'No assistant conversations recorded for this account yet.'
                : 'No conversations match.'}
            </div>
          )}
          {list.map((conv) => (
            <ConversationRow
              key={conv.Id}
              conv={conv}
              isSelected={selectedId === conv.Id}
              onSelect={() => setSelectedId(conv.Id)}
            />
          ))}
        </div>
      </div>

      {/* ── RIGHT PANE: transcript ───────────────────────────────────── */}
      <div className="convo-transcript" style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        border: '1px solid var(--border)', borderRadius: 10,
        background: 'var(--bg2)', overflow: 'hidden',
      }}>
        {!selectedId && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 13 }}>
            Select a conversation on the left to view the transcript.
          </div>
        )}
        {detailLoading && (
          <div style={{ padding: 20, color: 'var(--text3)', fontSize: 13 }}>Loading transcript…</div>
        )}
        {detailError && (
          <div style={{ padding: 14, color: '#f87171', fontSize: 13 }}>{detailError}</div>
        )}
        {detail && !detailLoading && (
          <TranscriptView
            conversation={detail.conversation}
            messages={detail.messages}
            visitor={detail.visitor}
            onEscalate={() => setShowEscalate(true)}
          />
        )}
      </div>

      {showEscalate && detail && (
        <EscalateModal
          conversation={detail.conversation}
          onClose={() => setShowEscalate(false)}
          onSuccess={handleEscalateSuccess}
        />
      )}
    </div>
  );
}

// ─── Filter bar ─────────────────────────────────────────────────────────
function FilterBar({ filters, onChange, onRefresh }) {
  const inp = {
    fontSize: 12, padding: '6px 8px', borderRadius: 6,
    background: 'var(--bg)', color: 'var(--text)',
    border: '1px solid var(--border)', outline: 'none',
  };
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 8,
      padding: 12, border: '1px solid var(--border)', borderRadius: 10,
      background: 'var(--bg2)', alignItems: 'center',
    }}>
      <select style={inp} value={filters.source} onChange={e => onChange({ ...filters, source: e.target.value })}>
        {SOURCES.map(s => <option key={s} value={s}>{s ? `Source: ${SOURCE_LABEL[s] || s}` : 'All sources'}</option>)}
      </select>
      <select style={inp} value={filters.intent} onChange={e => onChange({ ...filters, intent: e.target.value })}>
        {INTENTS.map(s => <option key={s} value={s}>{s ? `Intent: ${s}` : 'All intents'}</option>)}
      </select>
      <input
        style={{ ...inp, flex: 1, minWidth: 120 }}
        type="date"
        value={filters.since}
        onChange={e => onChange({ ...filters, since: e.target.value })}
        placeholder="Since"
        title="Started on or after"
      />
      <button
        onClick={onRefresh}
        style={{ ...inp, cursor: 'pointer', background: 'var(--bg3)' }}
        title="Refresh"
      >↻</button>
    </div>
  );
}

// ─── Conversation row in list ──────────────────────────────────────────
function ConversationRow({ conv, isSelected, onSelect }) {
  const isEscalated = !!conv.Resolved_To_Case__c;
  const accountName = conv.Account__r?.Name || 'Anonymous';
  return (
    <button
      onClick={onSelect}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        padding: '10px 14px', border: 'none', cursor: 'pointer',
        background: isSelected ? 'rgba(99,102,241,0.12)' : 'transparent',
        borderLeft: isSelected ? '2px solid var(--accent)' : '2px solid transparent',
        borderBottom: '1px solid var(--border-soft, rgba(255,255,255,0.04))',
        fontFamily: 'inherit',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{conv.Name}</span>
        <span style={{ fontSize: 10, color: 'var(--text3)' }}>{fmtDate(conv.Started_At__c)}</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text2, rgba(255,255,255,0.7))', marginTop: 2 }}>
        {accountName} · {SOURCE_LABEL[conv.Source__c] || conv.Source__c}
        {conv.visitorCountry && ` · ${conv.visitorCountry}`}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
        {conv.Last_Intent__c && <Pill label={conv.Last_Intent__c} variant="neutral" />}
        {isEscalated && <Pill label="Escalated" variant="warning" />}
        <Pill label={`${fmtTokens(conv.Total_Input_Tokens__c)}↓ / ${fmtTokens(conv.Total_Output_Tokens__c)}↑`} variant="muted" />
      </div>
    </button>
  );
}

// ─── Transcript view ──────────────────────────────────────────────────
function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function fmtDuration(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const mins = Math.round((new Date(endIso) - new Date(startIso)) / 60000);
  if (!(mins >= 0)) return null;
  return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function visitorLocalTime(iso, timezone) {
  if (!iso || !timezone) return null;
  try {
    return new Date(iso).toLocaleTimeString('en-US', { timeZone: timezone, hour: 'numeric', minute: '2-digit' });
  } catch { return null; }
}

// Everything chat capture knows about the person, in one line per question:
// where (browser timezone, not IP), on what, and have they been here before.
function VisitorStrip({ conversation, messages, visitor }) {
  if (!visitor) return null;
  const place = [visitor.city, visitor.country].filter(Boolean).join(', ');
  const local = visitorLocalTime(conversation.Started_At__c, visitor.timezone);
  const duration = fmtDuration(conversation.Started_At__c, conversation.Ended_At__c);
  const cost = conversation.Estimated_Cost_USD__c;
  const chats = visitor.chats;

  const items = [
    {
      label: 'Where',
      value: place || (visitor.placeReadable ? 'Unknown' : 'Not readable'),
      hint: visitor.placeReadable
        ? `Browser timezone ${visitor.timezone || '—'}. Device setting, not IP — a VPN or traveller shows their home zone.`
        : 'Timezone__c is not readable by the CRM integration user — grant it the Chat_Capture_Access permission set.',
      sub: [local && `${local} their time`, visitor.locale].filter(Boolean).join(' · '),
    },
    { label: 'Device', value: visitor.device || 'Unknown', hint: conversation.User_Agent__c || '' },
    {
      label: 'Visitor',
      value: chats ? (chats.total > 1 ? `${ordinal(chats.ordinal || chats.total)} of ${chats.total} chats` : 'First chat') : '—',
      hint: conversation.Anonymous_Visitor_Id__c ? `Visitor id ${conversation.Anonymous_Visitor_Id__c}` : '',
      highlight: chats && chats.total > 1,
    },
    {
      label: 'Session',
      value: [duration, `${messages.length} ${messages.length === 1 ? 'msg' : 'msgs'}`].filter(Boolean).join(' · '),
      sub: cost != null ? `$${Number(cost).toFixed(4)}` : '',
    },
  ];

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      borderBottom: '1px solid var(--border)', background: 'var(--bg)',
    }}>
      {items.map((it) => (
        <div key={it.label} title={it.hint} style={{ padding: '8px 18px', minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text3)' }}>{it.label}</div>
          <div style={{
            fontSize: 12, fontWeight: 600, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            color: it.highlight ? '#fbbf24' : 'var(--text)',
          }}>{it.value}</div>
          {it.sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function TranscriptView({ conversation, messages, visitor, onEscalate }) {
  const isEscalated = !!conversation.Resolved_To_Case__c;
  return (
    <>
      <div style={{
        padding: '14px 18px', borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{conversation.Name}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
            {conversation.Account__r?.Name || 'Anonymous'} ·{' '}
            {SOURCE_LABEL[conversation.Source__c] || conversation.Source__c} ·{' '}
            {messages.length} {messages.length === 1 ? 'turn' : 'turns'} ·{' '}
            started {fmtDate(conversation.Started_At__c)}
            {conversation.Ended_At__c && ` · ended ${fmtDate(conversation.Ended_At__c)}`}
          </div>
        </div>
        {isEscalated ? (
          <a
            href={`/cases/${conversation.Resolved_To_Case__c}`}
            style={{
              fontSize: 12, padding: '6px 12px', borderRadius: 6,
              background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
              color: '#fbbf24', textDecoration: 'none', whiteSpace: 'nowrap',
            }}
          >
            View case →
          </a>
        ) : (
          <button data-write
            onClick={onEscalate}
            style={{
              fontSize: 12, padding: '6px 14px', borderRadius: 6,
              background: 'var(--accent)', color: '#fff',
              border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            Escalate to Case
          </button>
        )}
      </div>
      <VisitorStrip conversation={conversation} messages={messages} visitor={visitor} />
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {messages.map((m) => <MessageBlock key={m.Id} message={m} />)}
        {messages.length === 0 && (
          <div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: 24 }}>
            No messages captured yet for this conversation.
          </div>
        )}
      </div>
    </>
  );
}

function MessageBlock({ message }) {
  const palette = ROLE_COLOR[message.Role__c] || ROLE_COLOR.assistant;
  return (
    <div style={{
      background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: 8,
      padding: '10px 14px', marginBottom: 10,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 6, fontSize: 11, color: palette.label, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
      }}>
        <span>{message.Role__c} · #{message.Sequence__c}</span>
        <span style={{ fontWeight: 400, color: 'var(--text3)', textTransform: 'none', letterSpacing: 0 }}>
          {message.Latency_Ms__c != null && `${message.Latency_Ms__c}ms · `}
          {message.Input_Tokens__c != null && message.Input_Tokens__c > 0 && `${fmtTokens(message.Input_Tokens__c)}↓ / `}
          {message.Output_Tokens__c != null && message.Output_Tokens__c > 0 && `${fmtTokens(message.Output_Tokens__c)}↑`}
        </span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
        {message.Content__c || <span style={{ color: 'var(--text3)' }}>(empty)</span>}
      </div>
      {message.Tool_Calls__c && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--text3)' }}>Tool calls</summary>
          <pre style={{ margin: '6px 0 0', padding: 8, background: 'rgba(0,0,0,0.2)', borderRadius: 4, fontSize: 11, color: 'var(--text2)', overflowX: 'auto' }}>
            {message.Tool_Calls__c}
          </pre>
        </details>
      )}
    </div>
  );
}

// ─── Pill / chip ───────────────────────────────────────────────────────
function Pill({ label, variant = 'neutral' }) {
  const palette = {
    neutral: { bg: 'rgba(255,255,255,0.06)', color: 'var(--text2, rgba(255,255,255,0.7))' },
    muted:   { bg: 'rgba(255,255,255,0.04)', color: 'var(--text3)' },
    warning: { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24' },
    success: { bg: 'rgba(16,185,129,0.15)', color: '#34d399' },
  }[variant] || {};
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 10, fontWeight: 600, ...palette,
    }}>
      {label}
    </span>
  );
}

// ─── Escalate modal ───────────────────────────────────────────────────
function EscalateModal({ conversation, onClose, onSuccess }) {
  const [subject, setSubject]         = useState(`Chat escalation — ${conversation.Name}`);
  const [description, setDescription] = useState('');
  const [priority, setPriority]       = useState('Medium');
  const [submitting, setSubmitting]   = useState(false);
  const [created, setCreated]         = useState(null);
  const [error, setError]             = useState('');

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await escalateConversation(conversation.Id, { subject, description, priority });
      setCreated({ caseId: res.caseId, caseNumber: res.caseNumber });
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  // Once the Case exists, every way out of the modal must run onSuccess so
  // the list/detail get the "already escalated" patch.
  const finish = () => onSuccess(created.caseId);

  if (created) return (
    <div onClick={finish} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 28, width: 400, maxWidth: '90vw', textAlign: 'center',
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%', margin: '0 auto 14px',
          background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#34d399', fontSize: 22, fontWeight: 700,
        }}>✓</div>
        <h2 style={{ margin: '0 0 12px', fontSize: 17 }}>Case created</h2>
        <div style={{
          background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.3)',
          borderRadius: 8, padding: '10px 14px', marginBottom: 14,
        }}>
          <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Case number</div>
          <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums', wordBreak: 'break-all' }}>
            {created.caseNumber ? `#${created.caseNumber}` : created.caseId}
          </div>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text3)' }}>
          Linked to the conversation — it now shows as escalated.
        </p>
        <button onClick={finish} style={{ padding: '8px 26px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>Done</button>
      </div>
    </div>
  );

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 24, width: 480, maxWidth: '90vw',
      }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Escalate to Case</h2>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text3)' }}>
          Creates a new Case linked to {conversation.Account__r?.Name ? `“${conversation.Account__r.Name}”` : 'this conversation'} and stamps it on the conversation so the loop is closed.
        </p>
        <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 4 }}>Subject</label>
        <input
          value={subject}
          onChange={e => setSubject(e.target.value)}
          style={{ width: '100%', padding: 10, fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box', marginBottom: 12 }}
          maxLength={255}
        />
        <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 4 }}>Description (optional)</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={4}
          style={{ width: '100%', padding: 10, fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box', marginBottom: 12, resize: 'vertical', fontFamily: 'inherit' }}
          placeholder="What's happening, what does the customer need…"
        />
        <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 4 }}>Priority</label>
        <select
          value={priority}
          onChange={e => setPriority(e.target.value)}
          style={{ width: '100%', padding: 10, fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box', marginBottom: 16 }}
        >
          <option>High</option>
          <option>Medium</option>
          <option>Low</option>
        </select>
        {error && <div style={{ padding: '8px 12px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', fontSize: 12, borderRadius: 6, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{ padding: '8px 18px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', cursor: submitting ? 'default' : 'pointer', fontFamily: 'inherit' }}
          >Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || !subject.trim()}
            style={{ padding: '8px 22px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: 'none', background: subject.trim() && !submitting ? 'var(--accent)' : 'var(--bg3)', color: '#fff', cursor: subject.trim() && !submitting ? 'pointer' : 'default', fontFamily: 'inherit' }}
          >{submitting ? 'Creating…' : 'Create Case'}</button>
        </div>
      </div>
    </div>
  );
}
